/**
 * Motion: Lenis for scroll, GSAP + ScrollTrigger for scroll-linked timelines.
 *
 * One effect here is the project's own, ported from
 * `reference/site/src/bubble-machine.js`: the pull-out cards that inflate on
 * the way in.
 *
 * The reference also had a second, page-wide bubble field made of CSS layers.
 * It is not here, and that was a measurement rather than a preference: ten
 * fixed elements running infinite animations cost about 1.2s of blocking time
 * against a 200ms budget, because a page that never stops animating never goes
 * idle. The machine emits real bubbles of its own, so the page has one bubble
 * system sourced from the artefact instead of two that merely look alike.
 *
 * Every timeline here must be skippable. `prefers-reduced-motion` is not an
 * edge case — it is a stated preference, and the core skin contract refuses a
 * skin that tries to opt out of respecting it. Under it, this whole module
 * returns before it builds anything and the cards are simply already there.
 */

import type { SkinMotion } from '@remidus/web-core'

import { driveHeroScroll, driveSteps, releaseHeroScroll, setHeroDocked } from './hero-scroll'
import { mountNav, setScroller } from './nav'

const reduced = (): boolean =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

/** Put the soap film on the one word of the title that names the subject.
 *
 *  Wraps the word so CSS can clip the film gradient to it. Runs in both the
 *  full and reduced-motion paths — the film is colour, not motion; only its
 *  sheen animates, and CSS turns that off on its own. Returns an undo, and
 *  wraps nothing rather than something wrong if the word is not there —
 *  the title is editable content. */
function filmTitleWord(root: HTMLElement): () => void {
  const title = root.querySelector<HTMLElement>('.rem-showcase__title')
  if (!title || title.querySelector('.skin-film-word')) return () => undefined
  const original = title.innerHTML
  const words = (title.textContent ?? '').split(/\s+/).filter(Boolean)
  if (!words.some((w) => /^bubble$/i.test(w))) return () => undefined
  title.innerHTML = words
    .map((w) => (/^bubble$/i.test(w) ? `<span class="skin-film-word">${w}</span>` : w))
    .join(' ')
  return () => {
    title.innerHTML = original
  }
}

export const motion: SkinMotion = {
  reducedMotion: 'respect',

  init: (root) => {
    // The section menu goes up first, before the reduced-motion return.
    //
    // `motion.init` is the only hook a skin gets on the rendered root, so this
    // is where it has to live — but navigation is not motion. A reader who has
    // asked for less movement still needs to get around eleven thousand pixels
    // of page, and returning early used to withhold the only means of doing so
    // from exactly the people most likely to want it.
    const teardown: (() => void)[] = [mountNav(root)]

    // The docked machine is fixed to the viewport, and the footer scrolls up
    // underneath it — on a narrow screen it sat squarely on the footer's
    // links. It yields: as the footer enters, the stage fades out, and it
    // comes back the moment the footer leaves. Watching the footer costs one
    // observer; reading scroll would cost arithmetic every frame.
    //
    // The finale takes it over for a second reason: on that card the
    // headpiece in the photograph is the machine, and two machines blowing
    // bubbles at once is one too many. The stage yields to either, so the
    // reasons are counted rather than toggled.
    const stage = root.querySelector<HTMLElement>('.rem-showcase__scene')
    if (stage) {
      const showing = new Set<Element>()
      const apply = (entries: IntersectionObserverEntry[]): void => {
        for (const entry of entries) {
          if (entry.isIntersecting) showing.add(entry.target)
          else showing.delete(entry.target)
        }
        stage.classList.toggle('skin-scene--yield', showing.size > 0)
      }
      const observers: IntersectionObserver[] = []

      const footer = document.querySelector('.rem-footer')
      if (footer) {
        const watch = new IntersectionObserver(apply)
        watch.observe(footer)
        observers.push(watch)
      }

      // The finale is a latch, not a window. Once the reader has reached it
      // the machine is done: it has handed its bubbles to the headpiece in
      // the photograph, and popping back for the two cards between the
      // finale and the footer would be a curtain call nobody asked for.
      // A root extended far upwards keeps an element "intersecting" after it
      // has scrolled past, which is exactly "reached or beyond".
      const finale = root.querySelector('.skin-step__card--collage')
      if (finale) {
        const latch = new IntersectionObserver(apply, {
          rootMargin: '100000px 0px 0px 0px',
        })
        latch.observe(finale)
        observers.push(latch)
      }
      teardown.push(() => observers.forEach((o) => o.disconnect()))
    }

    if (reduced()) {
      // Everything is already in its final state in the markup; reveals only
      // ever animate *to* that state, so there is nothing to undo here. The
      // film on the title is colour rather than motion, so it stays.
      teardown.push(filmTitleWord(root))
      root.classList.add('skin-motion--off')
      return () => teardown.forEach((fn) => fn())
    }

    void (async () => {
      const [{ default: Lenis }, { gsap }, { ScrollTrigger }] = await Promise.all([
        import('lenis'),
        import('gsap'),
        import('gsap/ScrollTrigger'),
      ])

      gsap.registerPlugin(ScrollTrigger)

      const lenis = new Lenis({
        duration: 1.05,
        easing: (t: number) => Math.min(1, 1.001 - 2 ** (-10 * t)),
        smoothWheel: true,
      })

      // Drive Lenis from GSAP's ticker rather than its own rAF, so scroll and
      // scroll-linked animation cannot drift a frame apart.
      const tick = (time: number) => lenis.raf(time * 1000)
      gsap.ticker.add(tick)
      gsap.ticker.lagSmoothing(0)
      lenis.on('scroll', ScrollTrigger.update)

      // The menu scrolls through Lenis rather than around it. `scrollIntoView`
      // and a smooth-scroll library both animate the same scrollTop, and the
      // two fight to a standstill part-way down.
      setScroller((target) => lenis.scrollTo(target, { offset: -96 }))

      teardown.push(() => {
        setScroller(null)
        gsap.ticker.remove(tick)
        lenis.destroy()
      })

      // ── The hero pin ─────────────────────────────────────────────────
      // The stage is about 500px tall and, left alone, scrolls away before the
      // machine has done anything. Pinning it holds it on screen for a viewport
      // and a bit while the whole performance plays out, which is what the
      // reference does and what the page was missing.
      //
      // `pinSpacing: false` because the stage is absolutely positioned and out
      // of flow: adding spacer height would push the article down by a
      // viewport for no reason. GSAP switches it to fixed for the duration.
      const stage = root.querySelector<HTMLElement>('.rem-showcase__scene')
      if (stage) {
        // The handover, and why it is a shrink and a grow rather than a move.
        //
        // Docking changes the stage's position, size and containing block all
        // at once. Transitioning between those two states means the machine
        // slides diagonally across the page while resizing, and there is no
        // easing that makes that read as anything but a glitch — the two
        // layouts have nothing in common to interpolate.
        //
        // So nothing interpolates. The machine shrinks to nothing where it is,
        // the layout swaps while there is nothing on screen to see it, and it
        // grows back at its perch. Two moves the eye can follow, instead of one
        // it cannot.
        //
        // The scale goes on the canvas, not the stage: the stage carries its
        // own `translateY(-50%)` when docked, and composing a scale onto that
        // means every change has to know about the other one.
        // Looked up on each use, not captured once. The WebGL layer is
        // deferred until after LCP, so at the moment this module initialises
        // there is no canvas yet — a reference taken here is null for the life
        // of the page, and every one of these calls silently does nothing.
        const canvasNow = (): HTMLElement | null => stage.querySelector<HTMLElement>('canvas')
        const puff = (k: number): void => {
          const canvas = canvasNow()
          if (!canvas) return
          canvas.style.transformOrigin = '50% 50%'
          canvas.style.transform = k >= 0.999 ? '' : `scale(${k})`
          canvas.style.opacity = k <= 0.02 ? '0' : ''
        }

        const dock = (on: boolean): void => {
          // Pinning writes inline width, height, top, left and position onto
          // the element, and an inline rule outranks any class. Clearing them
          // is what lets the docked stylesheet take over — without this the
          // class applies and the stage stays exactly where the pin left it.
          for (const prop of ['width', 'height', 'top', 'left', 'position', 'transform']) {
            stage.style.removeProperty(prop)
          }
          stage.classList.toggle('skin-scene--docked', on)
          setHeroDocked(on)
        }

        /** Swap the layout while the machine is invisible, then grow it back. */
        const arrive = (on: boolean): void => {
          const canvas = canvasNow()
          if (canvas) gsap.killTweensOf(canvas)
          puff(0)
          dock(on)
          if (!canvas) return
          // Read back a layout value so the browser applies the new geometry
          // before the tween starts. Without it the grow can be composited
          // against the old box and the machine appears to leap into place.
          void stage.offsetWidth
          gsap.fromTo(
            canvas,
            { scale: 0.04, opacity: 0 },
            {
              scale: 1,
              opacity: 1,
              duration: on ? 0.62 : 0.5,
              // A touch of overshoot on the way in. It is a bubble machine; it
              // should arrive like something inflating, not like a panel.
              ease: on ? 'back.out(1.9)' : 'power3.out',
              clearProps: 'transform,opacity',
            },
          )
        }
        const media = gsap.matchMedia()
        media.add('(min-width: 900px) and (min-height: 620px)', () => {
          // The machine holds its perch on the right for as long as the hero
          // copy actually is: pin until the bottom of the brief card has
          // scrolled past, not for a hard-coded viewport. The title and the
          // brief are what the machine is standing beside, so their combined
          // height is the pin's length — measured at refresh, so an edited
          // brief or a resized window moves the release point with it. A
          // viewport is kept as the floor so a short brief still leaves room
          // for the performance to play out.
          const brief = root.querySelector<HTMLElement>('.skin-pullout')
          const pin = ScrollTrigger.create({
            trigger: stage,
            start: 'top top',
            end: () => {
              if (!brief) return '+=100%'
              const scrolled = window.scrollY
              const stageTop = stage.getBoundingClientRect().top + scrolled
              const briefBottom = brief.getBoundingClientRect().bottom + scrolled
              return `+=${Math.max(window.innerHeight, briefBottom - stageTop)}`
            },
            pin: true,
            pinSpacing: false,
            scrub: true,
            refreshPriority: 10,
            onUpdate: (self) => {
              driveHeroScroll(self.progress, self.getVelocity() / 90)
              // Scrubbed, so the shrink is under the reader's thumb rather than
              // on a timer: it closes over the last sixth of the run and is
              // gone by the moment the pin releases.
              const out = Math.max(0, (self.progress - 0.84) / 0.16)
              puff(1 - out * out)
            },
            onLeave: () => {
              driveHeroScroll(1, 0)
              arrive(true)
            },
            onLeaveBack: () => {
              driveHeroScroll(0, 0)
              arrive(false)
            },
          })
          return () => {
            pin.kill()
            const canvas = canvasNow()
            if (canvas) gsap.killTweensOf(canvas)
            puff(1)
            stage.style.opacity = ''
            stage.style.pointerEvents = ''
          }
        })
        // Narrow and short viewports do not pin — there is nowhere to put a
        // pinned stage next to the copy — but the machine still turns as the
        // stage passes, so it is never a dead rectangle.
        media.add('(max-width: 899px), (max-height: 619px)', () => {
          const pass = ScrollTrigger.create({
            trigger: stage,
            start: 'top 85%',
            end: 'bottom top',
            scrub: 0.5,
            onUpdate: (self) => {
              driveHeroScroll(self.progress, self.getVelocity() / 90)
              const out = Math.max(0, (self.progress - 0.8) / 0.2)
              puff(1 - out * out)
            },
            onLeave: () => arrive(true),
            onEnterBack: () => arrive(false),
          })
          return () => {
            pass.kill()
            const canvas = canvasNow()
            if (canvas) gsap.killTweensOf(canvas)
            puff(1)
            dock(false)
          }
        })
        teardown.push(() => {
          media.revert()
          dock(false)
          releaseHeroScroll()
        })
      }

      // ── The march collage ────────────────────────────────────────────
      // Scroll assembles the scene: the backdrop settles, the cutout rises
      // to meet the reader, and each print tacks on in turn — all scrubbed,
      // so the tableau builds under the thumb rather than on a timer.
      const collage = root.querySelector<HTMLElement>('.skin-collage')
      if (collage) {
        const heroShot = collage.querySelector('.skin-collage__hero')
        
        const pieces = collage.querySelectorAll('.skin-collage__piece')
        const build = gsap.timeline({
          scrollTrigger: { trigger: collage, start: 'top 85%', end: 'top 25%', scrub: 0.5 },
        })
        if (heroShot) build.from(heroShot, { yPercent: 12, opacity: 0, duration: 0.45, ease: 'power2.out' }, 0)
        pieces.forEach((piece, index) => {
          build.from(
            piece,
            {
              y: 70,
              opacity: 0,
              rotation: index % 2 ? 12 : -12,
              scale: 1.12,
              duration: 0.3,
              ease: 'power2.out',
            },
            0.35 + index * 0.13,
          )
        })
      }

      // ── Bubble cards ─────────────────────────────────────────────────
      // Inflate coming in, pop going out, unpop coming back. Every bubble
      // surface, timeline steps included.
      //
      // The steps were held back from this because a card that pops while you
      // are still reading it is a card you cannot finish. They pop now because
      // that is what was asked for, and the trigger is tuned to earn it: a step
      // does not leave until its bottom has passed the top of the viewport, so
      // the pop happens behind you rather than under your eye.
      for (const card of root.querySelectorAll<HTMLElement>('.skin-bubble-surface')) {
        const isStep = card.classList.contains('skin-step__card')

        // A card already on screen when motion starts gets no scroll trigger at
        // all. It is created past its own start line, so onEnter never fires
        // and the card sits at the opacity-0 state it was set to — rendered,
        // laid out, invisible. That was the opening paragraph's whole problem.
        //
        // Not "inflate it and keep the trigger", either: the trigger's own
        // leave behaviour then pops it back out. The card is on screen to be
        // read from the first frame, and something you are already reading has
        // no business animating.
        if (card.getBoundingClientRect().top < window.innerHeight * 0.92) {
          gsap.set(card, { opacity: 1, scale: 1 })
          continue
        }
        const burst = document.createElement('span')
        burst.className = 'skin-burst'
        burst.setAttribute('aria-hidden', 'true')
        card.append(burst)
        card.classList.add('skin-inflate')
        // No blur in the tween. The reference blurred on the way in and out; a
        // blurred element is rasterised rather than composited, so a large one
        // is re-rendered on the CPU every frame of the tween. Scale and opacity
        // read as an inflating bubble on their own.

        // The start state is set here, not in the stylesheet, so a failed
        // bundle leaves a readable page rather than an invisible one.
        gsap.set(card, { opacity: 0, scale: 0.82 })

        const inflate = (fast: boolean) =>
          gsap.to(card, {
            opacity: 1,
            scale: 1,
            duration: fast ? 0.55 : 1.15,
            ease: fast ? 'back.out(1.7)' : 'elastic.out(1, 0.72)',
            overwrite: 'auto',
          })

        const trigger = ScrollTrigger.create({
          trigger: card,
          start: 'top 92%',
          // A step is long-form reading, so it holds until it is genuinely off
          // the top. A pull-out is a glance, so it can go earlier.
          end: isStep ? 'bottom top' : 'bottom 4%',
          onEnter: () => inflate(false),
          onEnterBack: () => inflate(true),
          onLeaveBack: () =>
            gsap.to(card, {
              opacity: 0,
              scale: 0.82,
              duration: 0.45,
              ease: 'power2.in',
              overwrite: 'auto',
            }),
          onLeave: () => {
            gsap.killTweensOf(burst)
            gsap.fromTo(
              burst,
              { opacity: 0.9, scale: 1 },
              { opacity: 0, scale: 1.09, duration: 0.42, ease: 'power2.out' },
            )
            gsap.to(card, {
              opacity: 0,
              scale: 1.05,
              duration: 0.26,
              ease: 'power2.in',
              overwrite: 'auto',
            })
          },
        })
        teardown.push(() => trigger.kill())
      }

      // ── The docked machine turns with the steps ──────────────────────
      // One scrubbed trigger over the whole timeline, normalised. The machine
      // does not drift on its own down here: it is pointed by where the reader
      // is. Because the mapping is proportional rather than per-card, adding or
      // deleting a step changes what each one maps to and nothing needs
      // re-tuning or re-rendering.
      const spine = root.querySelector<HTMLElement>('.skin-timeline')
      if (spine) {
        const turn = ScrollTrigger.create({
          trigger: spine,
          start: 'top 75%',
          end: 'bottom 60%',
          scrub: 0.7,
          onUpdate: (self) => driveSteps(self.progress),
        })
        teardown.push(() => turn.kill())
      }

      // ── Timeline ─────────────────────────────────────────────────────
      // The spine fills as the reader descends and each marker fills as its
      // step arrives. Scrubbed rather than triggered, so dragging the scrollbar
      // backwards unwinds it instead of leaving the line stranded full.
      // Every timeline on the page: the story is split around the CAD
      // viewer, so there is more than one.
      for (const timeline of root.querySelectorAll<HTMLElement>('.skin-timeline')) {
        const progress = timeline.querySelector<HTMLElement>('.skin-timeline__progress')
        if (progress) {
          const fill = ScrollTrigger.create({
            trigger: timeline,
            start: 'top 70%',
            end: 'bottom 70%',
            scrub: 0.4,
            onUpdate: (self) => {
              progress.style.transform = `scaleY(${self.progress})`
            },
          })
          teardown.push(() => fill.kill())
        }

        for (const step of timeline.querySelectorAll<HTMLElement>('.skin-step')) {
          const marker = ScrollTrigger.create({
            trigger: step,
            start: 'top 70%',
            onToggle: (self) => step.classList.toggle('is-past', self.isActive || self.progress > 0),
          })
          teardown.push(() => marker.kill())
        }
      }

      // ── Section reveals ──────────────────────────────────────────────
      const sections = root.querySelectorAll<HTMLElement>(
        '[data-remidus-block]:not(.skin-bubble-surface):not(:has(.skin-bubble-surface))',
      )
      for (const section of sections) {
        const animation = gsap.from(section, {
          opacity: 0,
          y: 18,
          duration: 0.7,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: section,
            start: 'top 85%',
            once: true,
          },
        })
        teardown.push(() => animation.kill())
      }

      // ── Media parallax ───────────────────────────────────────────────
      // The picture drifts against its card as the card passes. Small: eight
      // percent of the card's height over the whole pass. Enough that the two
      // columns are not one flat rectangle, not so much that a caption ever
      // separates from what it is captioning.
      for (const media of root.querySelectorAll<HTMLElement>('.skin-step__media')) {
        const inner = media.querySelector<HTMLElement>(':scope > *')
        if (!inner) continue
        const drift = ScrollTrigger.create({
          trigger: media,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 0.6,
          onUpdate: (self) => {
            inner.style.transform = `translate3d(0, ${(self.progress - 0.5) * -8}%, 0)`
          },
        })
        teardown.push(() => drift.kill())
      }

      // ── Step content ─────────────────────────────────────────────────
      // The card inflates as one; its contents arrive a beat behind it and
      // offset from each other. Without this the card is a single object that
      // appears, which reads as a slide rather than as something assembling.
      for (const card of root.querySelectorAll<HTMLElement>('.skin-step__card')) {
        const parts = card.querySelectorAll<HTMLElement>(
          '.skin-pullout__kicker, .skin-step__title, .skin-step__body p, .skin-step__specs',
        )
        if (parts.length === 0) continue
        // Movement only: the card carries the fade now that it inflates, and
        // two opacity tweens on nested elements multiply into a muddy reveal.
        gsap.set(parts, { y: 14 })
        const play = gsap.to(parts, {
          y: 0,
          duration: 0.55,
          ease: 'power2.out',
          stagger: 0.06,
          scrollTrigger: { trigger: card, start: 'top 88%', once: true },
        })
        teardown.push(() => play.kill())
      }

      // ── Section headings ─────────────────────────────────────────────
      // Wiped in on a clip rather than faded. A heading that fades is a
      // heading that is briefly unreadable; a wipe is legible the whole way.
      for (const heading of root.querySelectorAll<HTMLElement>('h2')) {
        const wipe = gsap.fromTo(
          heading,
          { clipPath: 'inset(0 100% 0 0)', y: 10 },
          {
            clipPath: 'inset(0 0% 0 0)',
            y: 0,
            duration: 0.85,
            ease: 'power3.out',
            scrollTrigger: { trigger: heading, start: 'top 86%', once: true },
          },
        )
        teardown.push(() => wipe.kill())
      }

      // ── The hero title ───────────────────────────────────────────────
      // Split into lines and staggered. The split is done on rendered line
      // boxes rather than on words, so it survives whatever width the title
      // wraps at, and it is undone if anything throws — a title that fails to
      // animate must still be a title.
      const title = root.querySelector<HTMLElement>('.rem-showcase__title')
      if (title) {
        const original = title.innerHTML
        let targets: HTMLElement[] = [title]
        try {
          const words = (title.textContent ?? '').split(/\s+/).filter(Boolean)
          title.innerHTML = words
            .map(
              (w) =>
                `<span class="skin-word"><span${/^bubble$/i.test(w) ? ' class="skin-film-word"' : ''}>${w}</span></span>`,
            )
            .join(' ')
          targets = Array.from(title.querySelectorAll<HTMLElement>('.skin-word > span'))
          if (targets.length === 0) {
            title.innerHTML = original
            targets = [title]
          }
        } catch {
          title.innerHTML = original
          targets = [title]
        }
        const intro = gsap.from(targets, {
          yPercent: 118,
          duration: 1.0,
          ease: 'power4.out',
          stagger: 0.055,
          delay: 0.08,
        })
        teardown.push(() => {
          intro.kill()
          title.innerHTML = original
        })
      }

      // ── The standfirst and the rule under it ─────────────────────────
      const lede = root.querySelector<HTMLElement>('.rem-showcase__hero + * , .rem-showcase__lede')
      if (lede) {
        const rise = gsap.from(lede, {
          opacity: 0,
          y: 20,
          duration: 0.9,
          ease: 'power3.out',
          delay: 0.45,
        })
        teardown.push(() => rise.kill())
      }

      teardown.push(() => ScrollTrigger.getAll().forEach((t) => t.kill()))
    })()

    return () => teardown.forEach((fn) => fn())
  },
}

export default motion
